import { useId } from 'react';
import type { Reputation } from '@teentack/shared';

/**
 * A rating, shown as stars.
 *
 * Two rules keep this honest. An unrated person shows "No reviews yet" rather
 * than an empty five-star row, which reads as a zero. And the count always sits
 * beside the average, because one five-star review is not the claim that forty
 * are. [Job Poster step 14]
 */

function Star({ fill }: { fill: number }) {
  // A gradient stop rather than a half-star glyph, so 4.3 renders as 4.3.
  // The id comes from useId, not Math.random: a fresh id every render would
  // break the fill reference mid-paint and can collide across instances.
  const id = useId();
  return (
    <svg viewBox="0 0 20 20" className="size-4 shrink-0" aria-hidden="true">
      <defs>
        <linearGradient id={id}>
          <stop offset={`${fill * 100}%`} stopColor="currentColor" />
          <stop offset={`${fill * 100}%`} stopColor="transparent" />
        </linearGradient>
      </defs>
      <path
        d="M10 1.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L1.6 7.7l5.8-.8z"
        fill={`url(#${id})`}
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function StarRating({ value, className = '' }: { value: number; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-0.5 text-sun-500 ${className}`}>
      {[0, 1, 2, 3, 4].map((index) => (
        <Star key={index} fill={Math.max(0, Math.min(1, value - index))} />
      ))}
    </span>
  );
}

/** The compact score shown beside a name in a list. */
export function ReputationBadge({
  reputation,
  size = 'sm',
}: {
  reputation: Reputation | undefined;
  size?: 'sm' | 'md';
}) {
  if (!reputation || reputation.count === 0) {
    return (
      <span
        className={`inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 font-semibold text-slate-500 ${
          size === 'sm' ? 'text-[0.7rem]' : 'text-xs'
        }`}
      >
        No reviews yet
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <StarRating value={reputation.average ?? 0} />
      <span
        className={`font-bold text-ink ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
        // Digits line up when several of these stack in a list.
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {reputation.average?.toFixed(1)}
      </span>
      <span className={`text-slate-400 ${size === 'sm' ? 'text-[0.7rem]' : 'text-xs'}`}>
        ({reputation.count})
      </span>
    </span>
  );
}

/** The star picker used when writing a review. */
export function StarPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: number;
  onChange: (rating: number) => void;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="Rating" className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((rating) => (
        <button
          key={rating}
          type="button"
          role="radio"
          aria-checked={value === rating}
          aria-label={`${rating} out of 5`}
          disabled={disabled}
          onClick={() => onChange(rating)}
          className={`rounded-lg p-1 transition disabled:opacity-50 ${
            rating <= value ? 'text-sun-500' : 'text-slate-300 hover:text-sun-300'
          }`}
        >
          <svg viewBox="0 0 20 20" className="size-7" aria-hidden="true">
            <path
              d="M10 1.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L1.6 7.7l5.8-.8z"
              fill="currentColor"
            />
          </svg>
        </button>
      ))}
    </div>
  );
}
